import React, { useEffect, useState } from "react";
import "./ManageLibrary.css";
import ReusableTable, {
  Column,
} from "../../../components/reusable-table/ReusableTable";
import Button from "../../../components/reusable-button/Button";
import LibraryModal, { LibraryFormData } from "./library-modal/LibraryModal";
import { BiEditAlt } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLibraries,
  createLibraryThunk,
  updateLibraryThunk,
  setLibraryRefresh,
  LibraryRow,
} from "../../../redux/slices/librarySlice";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { RootState, AppDispatch } from "../../../redux/store/store";
import ReusableLoader from "../../../components/reusable-loader/ReusableLoader";

const EditIcon = BiEditAlt as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

const ManageLibrary: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    data: libraries,
    authTypes,
    loading,
    error,
    needRefresh,
  } = useSelector((state: RootState) => state.library);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editData, setEditData] = useState<LibraryFormData | undefined>();

  const columns: Column<LibraryRow>[] = [
    {
      header: "Library",
      render: (lib) => <span className="library-name">{lib.title}</span>,
    },
    {
      header: "Connection Type",
      render: (lib) => (
        <span className="library-name">
          {lib?.connectionType?.toUpperCase() || "-"}
        </span>
      ),
    },
    { header: "Auth Type", accessor: "description" },
    {
      header: "Status",
      render: (lib) => (
        <span
          className={`status ${
            lib.status === "Active" ? "active" : "inactive"
          }`}
        >
          {lib.status}
        </span>
      ),
    },
    {
      header: "",
      render: (lib) => (
        <button
          className="edit-button"
          onClick={() => {
            setModalMode("edit");
            const config = lib.originalConfig?.[0];
            const isPatron = config && "hostname" in config;
            const isSip = config && "sipSERVER" in config;

            setEditData({
              id: lib.id,
              name: lib.title,
              authType: lib.authTypeId || "",
              active: lib.status === "Active",
              contactEmail: lib.contactEmail || "",
              hostname: isPatron ? (config as any).hostname ?? "" : "",
              port: isPatron ? (config as any).port?.toString() ?? "" : "",
              sipServer: isSip ? (config as any).sipSERVER ?? "" : "",
              sipPort: isSip ? (config as any).sipPORT?.toString() ?? "" : "",
              institutionId: isSip ? (config as any).institutionId ?? "" : "",
              sipUsername: isSip ? (config as any).sipUsername ?? "" : "",
              sipPassword: isSip ? (config as any).sipPassword ?? "" : "",
              connectionType: isSip ? (config as any).connectionType ?? "" : "",
            });

            setModalOpen(true);
          }}
        >
          <EditIcon style={{ fontSize: 16, color: "#28606B" }} />
        </button>
      ),
    },
  ];

  useEffect(() => {
    if (needRefresh) {
      dispatch(fetchLibraries());
    }
  }, [dispatch, needRefresh]);

  const handleSave = async (payload: LibraryFormData) => {
    const selectedAuthLabel = authTypes
      .find((opt) => opt.value === payload.authType)
      ?.label.toLowerCase();

    const apiPayload = {
      name: payload.name,
      contactEmail: payload.contactEmail || "",
      libraryAuthTypeId: payload.authType,
      isActive: payload.active,
      libraryConfig:
        selectedAuthLabel === "patron api"
          ? [
              {
                hostname: payload.hostname ?? "",
                port: Number(payload.port) || 0,
              },
            ]
          : [
              {
                sipSERVER: payload.sipServer ?? "",
                sipPORT: Number(payload.sipPort) || 0,
                sipUsername: payload.sipUsername ?? "",
                sipPassword: payload.sipPassword ?? "",
                institutionId: payload.institutionId ?? "",
                connectionType: payload.connectionType ?? "",
              },
            ],
    };

    try {
      if (modalMode === "add") {
        await dispatch(createLibraryThunk(apiPayload)).unwrap();
        dispatch(
          showSnackbar({
            message: "Library added successfully",
            type: "success",
          })
        );
      } else {
        await dispatch(
          updateLibraryThunk({ id: payload.id || "", payload: apiPayload })
        ).unwrap();
        dispatch(
          showSnackbar({
            message: "Library updated successfully",
            type: "success",
          })
        );
      }
      setModalOpen(false);
      dispatch(setLibraryRefresh(true));
    } catch (err) {
      dispatch(
        showSnackbar({
          message: (err as string) || "Failed to save",
          type: "error",
        })
      );
    }
  };

  return (
    <div className="manage-library">
      <div className="header">
        <h2>List Library ({libraries?.length})</h2>
        <div>
          <Button
            label="+ Add Library"
            onClick={() => {
              setModalMode("add");
              setEditData(undefined);
              setModalOpen(true);
            }}
          />
        </div>
      </div>

      {loading ? (
        <p>
          <ReusableLoader />
        </p>
      ) : error ? (
        <p className="error">⚠️ {error}</p>
      ) : (
        <ReusableTable columns={columns} rows={libraries} />
      )}

      <LibraryModal
        isOpen={modalOpen}
        mode={modalMode}
        initialData={editData}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        authOptions={authTypes}
      />
    </div>
  );
};

export default ManageLibrary;
